/**
 * Pure shaping for the supplier audit export. Turns raw Firestore document
 * data (already read by load.js) into the structures declared in types.js.
 * Nothing here touches React or Firestore, so every function can be unit
 * tested with plain objects.
 */

import moment from "moment"

import { BOARD_TYPES } from "../../../../utils/exports/board-types"
import { ROOM_TYPES } from "../../../../utils/exports/room-types"
import airlines from "../../../booking/tabs/_utils/airlines.json"
import { BUGGY_OPTIONS, TRANSFER_TYPES, UNKNOWN_SUPPLIER_NAME, UNNAMED_SUPPLIER_NAME, supplierKey } from "./types"

const CONFIRMED = "CONFIRMED"
const NOT_CONFIRMED = "NOT_CONFIRMED"
const APPROVED = "approved"

const KIND_ORDER = { hotel: 0, course: 1, transfer: 2 }

/**
 * Epoch millis from a Firestore Timestamp-like object, a Date or a number.
 * Anything else (including an invalid Date) gives null.
 *
 * @param {{ seconds: number }|Date|number|null|undefined} value
 * @returns {number|null}
 */
export const toMillis = (value) => {
	if (value === null || value === undefined) return null
	if (typeof value === "number") return Number.isFinite(value) ? value : null
	if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.getTime()
	if (typeof value.seconds === "number") return value.seconds * 1000
	return null
}

/**
 * Finite number or null. Numeric strings are accepted because a few legacy
 * items store costs as text.
 */
const toNumber = (value) => {
	if (value === null || value === undefined || value === "") return null
	const number = Number(value)
	return Number.isFinite(number) ? number : null
}

const toText = (value) => (value === null || value === undefined ? "" : String(value))

/**
 * Supplier currency codes are stored lower case ("gbp", "eur", "usd"), with a
 * handful of legacy "euro" records.
 */
const normaliseCurrency = (value) => {
	const code = toText(value).trim().toUpperCase()
	return code === "EURO" ? "EUR" : code
}

/**
 * @returns {import("./types").AuditSupplier}
 */
export const shapeSupplier = ({ id, kind, doc, cms }) => ({
	id: toText(id),
	kind,
	name: doc ? doc.name || UNNAMED_SUPPLIER_NAME : UNKNOWN_SUPPLIER_NAME,
	email: toText(doc?.email),
	currency: normaliseCurrency(doc?.currency),
	region: toText(cms?.region),
	country: toText(cms?.country),
	missing: !doc,
	cmsLinked: Boolean(cms),
})

/**
 * @param {Object|null} entry Latest confirmations document data
 * @returns {import("./types").AuditConfirmation}
 */
export const shapeConfirmation = (entry) => ({
	status: entry?.status === CONFIRMED || entry?.status === NOT_CONFIRMED ? entry.status : "",
	reference: toText(entry?.reference),
	contact: toText(entry?.confirmed_by),
	receivedMillis: toMillis(entry?.received_date) ?? toMillis(entry?.created),
	notes: toText(entry?.notes),
})

/**
 * @returns {import("./types").AuditInvoice}
 */
export const shapeInvoice = (id, data) => ({
	id: toText(id),
	amount: toNumber(data?.amount),
	currency: normaliseCurrency(data?.currency),
	status: toText(data?.payment_status),
	paidMillis: toMillis(data?.date_paid),
	externalReference: toText(data?.external_reference),
	internalReference: toText(data?.internal_reference),
})

/**
 * Count the room entries grouped by room_type, in first-seen order, and label
 * each type via ROOM_TYPES. Keys without a label are shown raw so nothing is
 * hidden from the office.
 *
 * @param {Array<{ room_type?: string }>|undefined} rooms
 * @returns {string} e.g. "2 x Twin Room, 1 x Single Room", "" for none
 */
export const summariseRooms = (rooms) => {
	if (!Array.isArray(rooms) || rooms.length === 0) return ""

	const counts = new Map()
	rooms.forEach((room) => {
		const key = room?.room_type || "Unspecified"
		counts.set(key, (counts.get(key) || 0) + 1)
	})

	return Array.from(counts.entries())
		.map(([key, count]) => `${count} x ${ROOM_TYPES[key] || key}`)
		.join(", ")
}

/**
 * Online direct bookings store preferred dinner times as a JSON string of
 * [{ date, time }] slots. Anything unreadable gives "".
 *
 * @param {string|undefined} preferredDinnerTimes
 * @returns {string} e.g. "12/10/2026 at 19:30; 13/10/2026 at 20:00"
 */
export const parseMealTimes = (preferredDinnerTimes) => {
	if (typeof preferredDinnerTimes !== "string" || preferredDinnerTimes.trim() === "") return ""

	let slots
	try {
		slots = JSON.parse(preferredDinnerTimes)
	} catch (error) {
		return ""
	}
	if (!Array.isArray(slots)) return ""

	return slots
		.map((slot) => {
			const date = toText(slot?.date).trim()
			const time = toText(slot?.time).trim()
			if (date && time) return `${date} at ${time}`
			return date || time
		})
		.filter(Boolean)
		.join("; ")
}

const isSupplierConfirmed = (confirmation, reservationStatus) => confirmation.status === CONFIRMED || reservationStatus === APPROVED

/**
 * @returns {import("./types").AuditHotel}
 */
export const shapeHotel = ({ id, data, supplier, invoices = [], confirmation, mealTimes = "" }) => {
	const item = data || {}
	const shapedConfirmation = confirmation || shapeConfirmation(null)
	const checkInMillis = toMillis(item.check_in)
	const nights = toNumber(item.nights) || 0
	const boardTypeKey = toText(item.board_type)
	const reservationStatus = toText(item.booking_status)

	return {
		id: toText(id),
		supplier,
		checkInMillis,
		checkOutMillis: checkInMillis === null ? null : moment(checkInMillis).add(nights, "days").valueOf(),
		nights,
		boardTypeKey,
		boardType: BOARD_TYPES[boardTypeKey] || "",
		rooms: summariseRooms(item.rooms),
		roomCount: Array.isArray(item.rooms) ? item.rooms.length : 0,
		mealTimes: toText(mealTimes),
		comments: toText(item.comments),
		reservationStatus,
		reservationComments: toText(item.booking_status_comments),
		confirmation: shapedConfirmation,
		supplierConfirmed: isSupplierConfirmed(shapedConfirmation, reservationStatus),
		cost: toNumber(item.total_cost),
		currency: supplier.currency,
		invoices,
	}
}

/**
 * Buggy label: the comp_buggies option where one is set, otherwise the legacy
 * `buggies` boolean (mirrors what the round component shows to agents).
 */
const buggyLabel = (item) => {
	if (item.comp_buggies && BUGGY_OPTIONS[item.comp_buggies]) return BUGGY_OPTIONS[item.comp_buggies]
	return BUGGY_OPTIONS[String(Boolean(item.buggies))]
}

/**
 * @returns {import("./types").AuditRound}
 */
export const shapeRound = ({ id, data, supplier, invoices = [], confirmation, golfers }) => {
	const item = data || {}
	const shapedConfirmation = confirmation || shapeConfirmation(null)
	const players = toNumber(golfers) || 0
	const reservationStatus = toText(item.booking_status)
	const costPerGolfer = toNumber(item.cost_per_golfer)
	const buggiesKey = item.comp_buggies && BUGGY_OPTIONS[item.comp_buggies] ? item.comp_buggies : ""

	return {
		id: toText(id),
		supplier,
		teeTimeMillis: toMillis(item.tee_time),
		players,
		buggiesKey,
		buggies: buggyLabel(item),
		buggiesBooked: Boolean(item.buggies_booked),
		buggiesCost: buggiesKey === "yes_prepaid" ? toNumber(item.buggies_cost) : null,
		buggiesUnitCost: toNumber(item.buggies_cost),
		comments: toText(item.comments),
		reservationStatus,
		reservationComments: toText(item.booking_status_comments),
		confirmation: shapedConfirmation,
		supplierConfirmed: isSupplierConfirmed(shapedConfirmation, reservationStatus),
		costPerGolfer,
		cost: costPerGolfer !== null && players > 0 ? Math.round(costPerGolfer * players * 100) / 100 : null,
		currency: supplier.currency,
		invoices,
	}
}

/**
 * @returns {import("./types").AuditTransport}
 */
export const shapeTransport = ({ booking, supplier = null, invoices = [], confirmation }) => {
	const data = booking || {}
	const shapedConfirmation = confirmation || shapeConfirmation(null)
	const transferTypeKey = toText(data.transfer_type)
	const reservationStatus = toText(data.transfer_booking_status)
	const lookup = data.transfers_lookup_selection || {}

	return {
		transferTypeKey,
		transferType: TRANSFER_TYPES[transferTypeKey] || "",
		airportTransferRequired: transferTypeKey === "airport" || transferTypeKey === "airport_and_golf",
		golfTransferRequired: transferTypeKey === "golf" || transferTypeKey === "airport_and_golf" || Boolean(data.golf_shuttles),
		supplier,
		transfersBooked: Boolean(data.transfers_booked),
		transfersReference: toText(data.transfers_reference),
		reservationStatus,
		reservationComments: toText(data.transfer_booking_comments),
		confirmation: shapedConfirmation,
		supplierConfirmed: isSupplierConfirmed(shapedConfirmation, reservationStatus),
		notes: toText(data.transfer_notes),
		lookupProvider: toText(lookup.supplier_name),
		lookupDescription: toText(lookup.short_description),
		lookupPickupTime: toText(lookup.pickup_time),
		cost: toNumber(data.margins?.transfers_cost),
		invoices,
		eventTransferBooked: Boolean(data.event_transfer_booked),
		eventTransferReference: toText(data.event_transfer_reference),
	}
}

/**
 * PaxPort supplier codes map to airline names in airlines.json.
 */
const airlineName = (code) => {
	if (!code) return ""
	const match = Object.entries(airlines).find(([, codes]) => codes.includes(code))
	return match ? match[0] : ""
}

/**
 * PaxPort stores dates as "YYYYMMDD" and times as "HHmm" in the local time of
 * the airport. Both parts are required and parsed strictly; anything else is
 * null rather than a guess.
 */
const flightMillis = (date, time) => {
	const dateText = toText(date).trim()
	const timeText = toText(time).trim()
	if (!/^\d{8}$/.test(dateText) || !/^\d{4}$/.test(timeText)) return null
	const parsed = moment(`${dateText}${timeText}`, "YYYYMMDDHHmm", true)
	return parsed.isValid() ? parsed.valueOf() : null
}

/**
 * @returns {import("./types").AuditFlights}
 */
export const shapeFlights = ({ booking, confirmation }) => {
	const data = booking || {}
	const flight = data.flight || null
	const cost = toNumber(data.margins?.flights_cost)
	const search = data.transfers_lookup_search || {}

	return {
		required: (cost !== null && cost > 0) || Boolean(flight),
		booked: Boolean(data.flights_booked),
		reference: toText(data.flights_reference),
		cost,
		provider: airlineName(flight?.supplier),
		outboundFlightNumber: toText(flight?.outbound_flight_number),
		originAirport: toText(flight?.origin_airport),
		arrivalAirport: toText(flight?.destination_airport),
		departureMillis: flightMillis(flight?.departure_date, flight?.departure_time),
		arrivalMillis: flightMillis(flight?.arrival_date, flight?.arrival_time),
		returnFlightNumber: toText(flight?.return_flight_number),
		returnDepartureMillis: flightMillis(flight?.return_home_depart_date, flight?.return_home_depart_time),
		lookupArrivalTime: toText(search.flight_ship_train_time),
		lookupDestination: toText(search.to_destination),
		confirmation: confirmation || shapeConfirmation(null),
	}
}

/**
 * The booking's margins object as stored. Hotel, round, buggy and transfer
 * costs are in the supplier currency; dividing by exchangeRate gives GBP (see
 * updateBookingMargins in src/utils/methods.js). Flights and miscellaneous
 * costs are already GBP. A missing or zero exchange rate is treated as 1 so
 * the export still shows a GBP figure; updateBookingMargins would have counted
 * those costs as 0, which the per-booking check column then flags.
 *
 * @returns {import("./types").AuditMargins}
 */
export const shapeMargins = (margins) => {
	const data = margins || {}
	const exchangeRate = toNumber(data.exchange_rate)

	return {
		customerPrice: toNumber(data.customer_price),
		exchangeRate: exchangeRate && exchangeRate > 0 ? exchangeRate : 1,
		hotelsCost: toNumber(data.hotels_cost),
		roundsCost: toNumber(data.rounds_cost),
		buggiesCost: toNumber(data.buggies_cost),
		transfersCost: toNumber(data.transfers_cost),
		flightsCost: toNumber(data.flights_cost),
		miscellaneousCosts: toNumber(data.miscellaneous_costs),
		supplierInvoices: toNumber(data.supplier_invoices),
		profit: toNumber(data.profit),
		agentsMargin: toNumber(data.agents_margin),
	}
}

const plural = (count, noun) => `${count} ${noun}${count === 1 ? "" : "s"}`

/**
 * "7 nights at Hotel X, Half Board, 3 rounds (A, B, C)". With more than one
 * hotel the stays are chained ("3 nights at A + 4 nights at B") and every
 * distinct board type is listed. Missing parts are simply left out.
 */
const packageSummary = (hotels, rounds) => {
	const parts = []

	if (hotels.length > 0) {
		parts.push(hotels.map((hotel) => `${plural(hotel.nights, "night")} at ${hotel.supplier.name}`).join(" + "))
		const boards = Array.from(new Set(hotels.map((hotel) => hotel.boardType).filter(Boolean)))
		if (boards.length > 0) parts.push(boards.join(" / "))
	}

	if (rounds.length > 0) {
		parts.push(`${plural(rounds.length, "round")} (${rounds.map((round) => round.supplier.name).join(", ")})`)
	}

	return parts.join(", ")
}

/**
 * @returns {import("./types").AuditBooking}
 */
export const shapeBooking = ({ id, data, client, agentName = "", packageTitle = "", hotels = [], rounds = [], transport, flights }) => {
	const booking = data || {}
	const golfers = toNumber(booking.golfers) || 0
	const nonGolfers = toNumber(booking.non_golfers) || 0

	return {
		id: toText(id),
		reference: toText(booking.reference),
		leadName: [toText(client?.first_name).trim(), toText(client?.last_name).trim()].filter(Boolean).join(" "),
		leadEmail: toText(client?.email),
		leadPhone: toText(client?.phone),
		agentName: toText(agentName),
		travelMillis: toMillis(booking.check_in),
		bookedMillis: toMillis(booking.booked),
		confirmed: Boolean(booking.confirmed),
		golfers,
		nonGolfers,
		pax: golfers + nonGolfers,
		packageTitle: toText(packageTitle),
		bookedLocation: toText(booking.booked_location),
		enquiryLocation: toText(booking.enquiry?.location),
		packageSummary: packageSummary(hotels, rounds),
		customerPrice: toNumber(booking.margins?.customer_price),
		margins: shapeMargins(booking.margins),
		paidByClient: toNumber(booking.paid_by_client),
		balanceOutstanding: toNumber(booking.balance_outstanding),
		resortsPaid: Boolean(booking.resorts_paid),
		eventTag: toText(booking.event_tag),
		hotels,
		rounds,
		transport,
		flights,
	}
}

/**
 * Every distinct supplier across hotels, rounds and transfers, with how many
 * bookings and items it appears on. Sorted hotels, then courses, then
 * transfers, alphabetically within each kind.
 *
 * @param {import("./types").AuditBooking[]} bookings
 * @returns {import("./types").AuditSupplierSummary[]}
 */
export const summariseSuppliers = (bookings) => {
	const summaries = new Map()

	const record = (supplier, bookingID) => {
		const key = supplierKey(supplier)
		if (!summaries.has(key)) summaries.set(key, { summary: { ...supplier, bookingCount: 0, itemCount: 0 }, bookingIDs: new Set() })
		const entry = summaries.get(key)
		entry.summary.itemCount += 1
		if (!entry.bookingIDs.has(bookingID)) {
			entry.bookingIDs.add(bookingID)
			entry.summary.bookingCount += 1
		}
	}

	bookings.forEach((booking) => {
		booking.hotels.forEach((hotel) => record(hotel.supplier, booking.id))
		booking.rounds.forEach((round) => record(round.supplier, booking.id))
		if (booking.transport?.supplier) record(booking.transport.supplier, booking.id)
	})

	return Array.from(summaries.values())
		.map((entry) => entry.summary)
		.sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.name.localeCompare(b.name))
}
